import { useCallback, useRef, useState } from "react";
import { findURIAndReplaceOrigin } from "../infrastructure/entrypoint/link";
import { post } from "../infrastructure/api/digipost-fetch";
import { ForetakMedLenker } from "../infrastructure/api/api";
import { KontaktInfo } from "./Registreringsflyt";
import { useUnrecoverableError } from "../infrastructure/errors/useUnrecoverableError";
import { useEntrypointContext } from "../infrastructure/entrypoint/EntrypointContext";

export interface ManuellKontaktinfo {
  orgNumber: string;
  orgName: string;
  firstName: string;
  lastName: string;
  companyEmail: string;
  personalEmail: string;
  comment?: string;
}

export function useRegistrerVirksomhet() {
  const { setUnrecoverableError, setCustomError } = useUnrecoverableError();
  // Ref is the authoritative flag for immediate guarding against double
  // submits; state mirrors it so the UI can react. Always update both via
  // setRegistering to keep them in sync.
  const isRegistering = useRef(false);
  const [isRegisteringState, setIsRegisteringState] = useState(false);
  const entrypoint = useEntrypointContext();

  const setRegistering = useCallback((value: boolean) => {
    isRegistering.current = value;
    setIsRegisteringState(value);
  }, []);

  const registrerVirksomhet = useCallback(
    (valgtForetak: ForetakMedLenker, kontaktInfo: KontaktInfo) => {
      if (isRegistering.current) return;
      setRegistering(true);

      const url = findURIAndReplaceOrigin("registrer", valgtForetak._links);

      post(url, kontaktInfo)
        .then(async (res) => {
          const locationHeader = res.headers.get("location");
          if (locationHeader) {
            window.location.href = locationHeader;
          } else {
            setRegistering(false);
            setCustomError("Registreringsrespons mangler location-header");
          }
        })
        .catch((e) => {
          setRegistering(false);
          setUnrecoverableError(e);
        });
    },
    [setRegistering, setUnrecoverableError, setCustomError],
  );

  const registrerVirksomhetManuelt = useCallback(
    (kontaktInfo: ManuellKontaktinfo, onSuccess: () => void) => {
      if (isRegistering.current) return;

      const url = findURIAndReplaceOrigin(
        "registrer-manuell",
        entrypoint._links,
      );
      setRegistering(true);

      post(url, kontaktInfo)
        .then(() => {
          setRegistering(false);
          onSuccess();
        })
        .catch((e) => {
          setRegistering(false);
          setUnrecoverableError(e);
        });
    },
    [entrypoint._links, setRegistering, setUnrecoverableError],
  );

  return {
    isRegistering: isRegisteringState,
    registrerVirksomhet,
    registrerVirksomhetManuelt,
  };
}
